import React, { useMemo } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { formatCurrency } from "../../utils/formatCurrency";
import { sinDiaEnCurso } from "./partialDay";

const fmt = (v) => formatCurrency(v ?? 0, true);

const pct = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

const Trend = ({ value, label, inline }) => {
  if (value == null || !isFinite(value)) return null;
  const up = value >= 0;
  return (
    <div
      className={`${up ? "text-success" : "text-danger"} ${inline ? "d-inline" : ""}`}
      style={{ fontSize: 11, fontWeight: 500 }}
    >
      <span className="material-symbols-rounded" style={{ fontSize: 12, verticalAlign: "middle" }}>
        {up ? "trending_up" : "trending_down"}
      </span>{" "}
      {up ? "+" : ""}
      {value.toFixed(1)}%{" "}
      <span className="text-muted" style={{ fontSize: 10 }}>
        {label}
      </span>
    </div>
  );
};

/**
 * `ocultar` es OPCIONAL y sirve para no repetir un numero que ya dice mejor
 * otro bloque de la misma pantalla.
 *
 * En Estadisticas 2 esta tira convive con el bloque "Como va hoy", que ya
 * dice cuantos autos hay adentro —y ademas cuantos lugares quedan— y cuanto
 * se cobro hoy —comparado contra un dia normal—. Repetirlo aca era el mismo
 * numero dos veces a 30 cm de distancia, con la version peor arriba.
 *
 * La pantalla de siempre no pasa nada y conserva las seis tarjetas.
 */
const KpiCards = ({ dailyDocs, liveData, ocultar = [] }) => {
  const kpis = useMemo(() => {
    const sorted = [...dailyDocs].sort((a, b) => b.date.localeCompare(a.date));

    // Last 30 days vs previous 30 days
    const last30 = sorted.slice(0, 30);

    const sumIncome = (arr) => arr.reduce((a, d) => a + (d.income || 0), 0);
    const sumTickets = (arr) => arr.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const sumCanceled = (arr) => arr.reduce((a, d) => a + (d.canceled_count || 0), 0);
    const sumTicketAmount = (arr) => arr.reduce((a, d) => a + (d.tickets?.total?.amount || 0), 0);
    const sumTicketOut = (arr) => arr.reduce((a, d) => a + (d.tickets?.total?.out || 0), 0);

    const l30Income = sumIncome(last30);
    const l30Tickets = sumTickets(last30);
    const l30Canceled = sumCanceled(last30);

    // Ventanas de días COMPLETOS para las comparaciones. Medir 30 días que
    // incluyen uno a medio facturar contra 30 días enteros muestra una caída
    // que no existe: a las 9 de la mañana el cartel diría "-3% vs 30d ant."
    // todos los días.
    const completos = sinDiaEnCurso(sorted);
    const last30C = completos.slice(0, 30);
    const prev30C = completos.slice(30, 60);
    const l30TicketAmount = sumTicketAmount(last30);
    const l30TicketOut = sumTicketOut(last30);

    // Ticket promedio: es un cociente entre dos magnitudes del mismo período,
    // así que el día parcial no lo distorsiona (entran juntos monto y cantidad).
    const avgTicket = l30TicketOut > 0 ? l30TicketAmount / l30TicketOut : 0;
    const p30TicketAmount = sumTicketAmount(prev30C);
    const p30TicketOut = sumTicketOut(prev30C);
    const prevAvgTicket = p30TicketOut > 0 ? p30TicketAmount / p30TicketOut : 0;

    // Promedios sobre días COMPLETOS. El total de 30 días SÍ incluye el día en
    // curso (es lo que el dueño espera ver al cobrar algo), pero dividir por él
    // como si fuera un día entero arrastra el promedio para abajo toda la
    // mañana y hace parecer que el negocio cayó.
    const l30IncomeC = sumIncome(last30C);
    const l30TicketsC = sumTickets(last30C);
    const dailyAvgIncome = last30C.length > 0 ? l30IncomeC / last30C.length : 0;
    const dailyAvgTickets = last30C.length > 0 ? l30TicketsC / last30C.length : 0;

    // Cancellation rate
    const cancelRate = l30Tickets > 0 ? (l30Canceled / l30Tickets) * 100 : 0;
    const cancelAmount = last30.reduce((a, d) => {
      const totalAmount = d.tickets?.total?.amount || 0;
      const totalOut = d.tickets?.total?.out || 0;
      const canceled = d.canceled_count || 0;
      if (totalOut === 0) return a;
      return a + (totalAmount / totalOut) * canceled;
    }, 0);

    return {
      // El TOTAL incluye hoy; la comparación se hace entre días completos.
      income30: l30Income,
      incomePct: pct(l30IncomeC, sumIncome(prev30C)),
      dailyAvgIncome,
      tickets30: l30Tickets,
      ticketsPct: pct(l30TicketsC, sumTickets(prev30C)),
      dailyAvgTickets,
      avgTicket,
      avgTicketPct: pct(avgTicket, prevAvgTicket),
      cancelRate,
      cancelAmount,
      canceled30: l30Canceled,
    };
  }, [dailyDocs]);

  // Occupancy from live
  const occ = liveData?.occupancy || {};
  const occPct = occ.pct || 0;
  // Para el dueno de una playa, LLENO ES LA BUENA NOTICIA. Antes >90% pintaba
  // rojo: el mejor dia del mes se veia como una alarma.
  // Lo unico que merece aviso es pasarse de la capacidad cargada (mal
  // configurada, o autos que entraron sin registrarse).
  const sinCapacidad = !occ.capacity;
  const occColor = sinCapacidad ? "secondary" : occPct > 100 ? "warning" : "success";

  return (
    <Row className="g-3">
      {/* Ocupacion */}
      {!ocultar.includes("ocupacion") && (
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0">
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>local_parking</span>
              Ocupación
            </div>
            <div className="d-flex align-items-end">
              <h3 className={`mb-0 text-${occColor}`}>{occ.current || 0}</h3>
              <span className="text-muted ms-1 mb-1" style={{ fontSize: 13 }}>
                {sinCapacidad ? "adentro" : `/ ${occ.capacity}`}
              </span>
            </div>
            <div className="progress mt-2" style={{ height: 5 }}>
              <div
                className={`progress-bar bg-${occColor}`}
                style={{ width: `${Math.min(occPct, 100)}%` }}
              />
            </div>
            {/* Un porcentaje solo no dice nada: "84%" puede ser una gran
                noticia o una alarma. La frase dice cuál de las dos es. */}
            <div className="text-muted text-end mt-1" style={{ fontSize: 10 }}>
              {sinCapacidad
                ? "cárgala en Configuración › General › Operación"
                : occPct > 100
                  ? `${occPct.toFixed(0)}% · hay más autos que lugares cargados`
                  : `${occPct.toFixed(0)}% de tu capacidad`}
            </div>
          </CardBody>
        </Card>
      </Col>
      )}

      {/* Ingresos 30d */}
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: "3px solid var(--bs-success)" }}>
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>attach_money</span>
              Ingresos 30 días
            </div>
            <h4 className="text-success mb-1">{fmt(kpis.income30)}</h4>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Promedio {fmt(kpis.dailyAvgIncome)} por día
            </div>
            <Trend value={kpis.incomePct} label="vs los 30 días previos" />
          </CardBody>
        </Card>
      </Col>

      {/* Tickets 30d */}
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: "3px solid var(--bs-primary)" }}>
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>confirmation_number</span>
              Tickets 30 días
            </div>
            <h4 className="text-primary mb-1">{kpis.tickets30}</h4>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Promedio {kpis.dailyAvgTickets.toFixed(0)} por día
            </div>
            <Trend value={kpis.ticketsPct} label="vs los 30 días previos" />
          </CardBody>
        </Card>
      </Col>

      {/* Avg ticket */}
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: "3px solid var(--bs-info)" }}>
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>receipt</span>
              Ticket promedio
            </div>
            <h4 className="text-info mb-1">{fmt(kpis.avgTicket)}</h4>
            <div className="text-muted" style={{ fontSize: 11 }}>
              lo que deja cada auto que sale
            </div>
            <Trend value={kpis.avgTicketPct} label="vs los 30 días previos" />
          </CardBody>
        </Card>
      </Col>

      {/* Cancellations */}
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: "3px solid var(--bs-warning)" }}>
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>block</span>
              Cancelaciones 30 días
            </div>
            <h4 className="text-warning mb-1">{kpis.canceled30}</h4>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {kpis.cancelRate.toFixed(1)}% de las entradas
            </div>
            {/* NO es plata perdida medida: es el ticket promedio por la
                cantidad de anulados. Muchas anulaciones son correcciones de
                carga, no plata que se fue. Va en gris y con la palabra
                "estimado", como en la pestaña de Tickets. */}
            <div className="text-muted" style={{ fontSize: 11 }}>
              {fmt(kpis.cancelAmount)} estimados si todos fueran cobrables
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* Hoy, en vivo */}
      {!ocultar.includes("hoy") && (
      <Col xl={2} lg={4} md={4} sm={6} xs={6}>
        <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: "3px solid var(--bs-dark)" }}>
          <CardBody className="py-3 px-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>today</span>
              Hoy
            </div>
            <h4 className="text-success mb-1">{fmt(liveData?.today?.income)}</h4>
            {/* Las salidas de caja y el neto de hoy ya viajaban al lado del
                ingreso y no se pintaban: se mostraba el bruto como si fuera el
                resultado. */}
            {liveData?.today?.expense > 0 && (
              <div style={{ fontSize: 12 }} className="mb-1">
                <span className="text-danger">− {fmt(liveData.today.expense)}</span>
                <span className="text-muted ms-1">salidas de caja</span>
              </div>
            )}
            {liveData?.today?.expense > 0 && (
              <div style={{ fontSize: 12, fontWeight: 600 }} className="mb-1">
                Neto {fmt(liveData?.today?.net)}
              </div>
            )}
            <div style={{ fontSize: 12 }}>
              <span className="text-success" title="Autos que entraron hoy">
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>arrow_forward</span>
                {liveData?.today?.tickets_in || 0}
              </span>
              <span className="text-danger ms-2" title="Autos que salieron hoy">
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>arrow_back</span>
                {liveData?.today?.tickets_out || 0}
              </span>
              {liveData?.today?.canceled > 0 && (
                <span className="text-warning ms-2" title="Tickets cancelados hoy">
                  <span className="material-symbols-rounded" style={{ fontSize: 13 }}>block</span>
                  {liveData.today.canceled}
                </span>
              )}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: 10 }}>
              entraron / salieron{liveData?.today?.canceled > 0 ? " / cancelados" : ""}
            </div>
          </CardBody>
        </Card>
      </Col>
      )}
    </Row>
  );
};

export default KpiCards;
